"use client";

import { useEffect } from "react";
import { logError } from "@/lib/analytics";

/**
 * Captures unhandled client-side errors and promise rejections,
 * logging them to the DB via logError().
 *
 * React render errors are caught by app/error.tsx (error boundary).
 * In production, React does NOT re-throw caught errors to window.onerror,
 * so there is no double-logging risk.
 *
 * This listener catches: third-party script errors, non-React runtime
 * errors, and unhandled promise rejections.
 *
 * Render once in providers.tsx.
 */
export const ErrorCapture = () => {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logError(event.error ?? event.message, {
        source: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      logError(event.reason ?? "Unhandled promise rejection", {
        source: "unhandledrejection",
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
};
